import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Clock, Flame, RefreshCw } from 'lucide-react';
import axios from 'axios';
import TaskCard from '../components/TaskCard';
import TopTaskCard from '../components/TopTaskCard';
import TaskDetailSheet from '../components/TaskDetailSheet';
import ProductDetailModal from '../components/ProductDetailModal';
import PartnerScroll from '../components/PartnerScroll';
import PromoBanner from '../components/PromoBanner';
import StatsStrip from '../components/StatsStrip';
import TaskFeedSkeleton from '../components/TaskFeedSkeleton';
import SupportFab from '../components/SupportFab';
import VersionFooter from '../components/VersionFooter';
import Toast from '../components/Toast';
import taskService from '../services/taskService';
import API_CONFIG from '../config/api';
import { formatMoney, getPlatformMeta, getCategoryMeta } from '../data/taskMeta';
import { useTranslation } from '../i18n/LocaleContext';

const PLATFORM_TABS = ['all', 'instagram', 'tiktok', 'youtube', 'facebook'];
const SORTS = [['default', 'sortDefault'], ['reward', 'sortReward'], ['ending', 'sortEnding']];
const DAILY_GOAL = 10;

const TasksPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState('all');
  const [category, setCategory] = useState(null);
  const [sort, setSort] = useState('default');
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);

  const load = useCallback(async () => {
    const [feed, sum] = await Promise.allSettled([taskService.getFeed(), taskService.getSummary()]);
    if (feed.status === 'fulfilled') setTasks(feed.value.tasks || []);
    if (sum.status === 'fulfilled') setSummary(sum.value.summary);
    setLoading(false);
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(API_CONFIG.getApiUrl('/jobs/all?page=1'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts((res.data.jobs || []).slice(0, 12));
    } catch (error) {
      console.error('Fetch products error:', error);
    }
  }, []);

  useEffect(() => { load(); loadProducts(); }, [load, loadProducts]);

  // 有待审核任务时轮询刷新：自动过审改到提交后随机 40–60s，靠这里把结果刷进界面，无需手动刷新。
  useEffect(() => {
    if (!Number(summary?.pendingReview || 0)) return undefined;
    const timer = setInterval(() => { load(); }, 5000);
    return () => clearInterval(timer);
  }, [summary?.pendingReview, load]);

  // 洗牌种子:每次刷新（手动或自动）换一次顺序，让列表内容看起来是新的
  const [shuffleSeed, setShuffleSeed] = useState(() => Date.now());

  const shuffleWith = useCallback((list, seed) => {
    let s = seed >>> 0;
    const rand = () => {
      s = (s * 1664525 + 1013904223) >>> 0;
      return s / 4294967296;
    };
    const out = list.slice();
    for (let i = out.length - 1; i > 0; i -= 1) {
      const j = Math.floor(rand() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  }, []);

  const topTasks = useMemo(() => {
    const list = tasks
      .filter((task) => task.isTop)
      .filter((task) => platform === 'all' || task.platform === platform);
    return shuffleWith(list, shuffleSeed).slice(0, 8);
  }, [tasks, platform, shuffleSeed, shuffleWith]);

  // 自动刷新：每 30 秒静默拉一次并换一版排序，标题旁图标转动提示
  const [refreshing, setRefreshing] = useState(false);
  const refresh = useCallback(async () => {
    setRefreshing(true);
    setShuffleSeed(Date.now());
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  useEffect(() => {
    const timer = setInterval(refresh, 30000);
    return () => clearInterval(timer);
  }, [refresh]);

  // Daily-goal intro: the bar fills from 0 and the counter ticks up to the
  // real value. Replays only when the actual count changes, not every 30s sync.
  const [goalAnim, setGoalAnim] = useState(0);
  const lastGoalRef = useRef(-1);
  useEffect(() => {
    if (!summary) return undefined;
    const limit = Math.max(1, Number(summary.dailyLimit) || DAILY_GOAL);
    const done = Math.min(Number(summary.usedToday || 0), limit);
    if (done === lastGoalRef.current) return undefined;
    lastGoalRef.current = done;
    if (done <= 0) { setGoalAnim(0); return undefined; }
    let i = 0;
    const timer = setInterval(() => {
      i += 1;
      setGoalAnim(i);
      if (i >= done) clearInterval(timer);
    }, 110);
    return () => clearInterval(timer);
  }, [summary]);

  const categories = useMemo(() => {
    const set = new Map();
    tasks.forEach((task) => {
      if (!task.category) return;
      if (platform !== 'all' && task.platform !== platform) return;
      set.set(task.category, (set.get(task.category) || 0) + 1);
    });
    return [...set.entries()].map(([name, count]) => ({ name, count }));
  }, [tasks, platform]);

  const visibleTasks = useMemo(() => tasks.filter((task) => {
    if (platform !== 'all' && task.platform !== platform) return false;
    if (category && task.category !== category) return false;
    return true;
  }), [tasks, platform, category]);

  const listedTasks = useMemo(() => {
    const list = shuffleWith(visibleTasks, shuffleSeed);
    if (sort === 'reward') return list.slice().sort((a, b) => b.reward - a.reward);
    if (sort === 'ending') return list.slice().sort((a, b) => a.remainingQuota - b.remainingQuota);
    return list;
  }, [visibleTasks, shuffleSeed, shuffleWith, sort]);

  if (loading) {
    return <TaskFeedSkeleton />;
  }

  // Allowance comes from the backend (VIP-level task_daily_limit); DAILY_GOAL is only the offline fallback.
  const goalLimit = summary ? Math.max(1, Number(summary.dailyLimit) || DAILY_GOAL) : DAILY_GOAL;
  const goalDone = summary ? Math.min(Number(summary.usedToday || 0), goalLimit) : 0;

  return (
    <div className="min-h-screen bg-canvas pb-24">
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      {/* Market header — balance right-aligned, confident but subordinate to the title */}
      <div className="sticky top-0 z-30 bg-white">
        <div className="max-w-md mx-auto px-4 pt-4 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <h1 className="text-[18px] font-extrabold text-ink tracking-tight">{t('taskMarket')}</h1>
              <button
                type="button"
                onClick={refresh}
                aria-label={t('taskRefresh')}
                className="w-8 h-8 -ml-0.5 flex items-center justify-center rounded-full active:bg-canvas"
              >
                <RefreshCw className={`w-[18px] h-[18px] text-ink-muted ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-ink-muted leading-none">{t('taskWallet')}</p>
              <p className="tabular text-[17px] font-extrabold text-money leading-tight mt-1">
                {formatMoney(summary?.balance ?? 0)}
              </p>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-4 overflow-x-auto scrollbar-hide border-b border-black/[0.06] -mb-px">
            {PLATFORM_TABS.map((key) => {
              const active = platform === key;
              const meta = key === 'all' ? null : getPlatformMeta(key);
              const Icon = meta && meta.Icon;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setPlatform(key); setCategory(null); }}
                  className={`shrink-0 flex items-center gap-1.5 pb-2.5 text-[13px] border-b-2 transition-colors ${
                    active ? 'border-brand-500 text-ink font-semibold' : 'border-transparent text-ink-muted font-medium'
                  }`}
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {key === 'all' ? t('taskFilter_all') : meta.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Earnings-flow banner — mirrors the real task UX with live task data */}
      <PromoBanner task={topTasks[0] || listedTasks[0]} balance={summary?.balance} />

      {/* Daily goal — full-bleed strip, edge-to-edge like real app list sections */}
      <div className="max-w-md mx-auto pt-2">
        <button
          type="button"
          onClick={() => navigate('/don-hang')}
          className="w-full bg-white border-y border-black/[0.05] px-4 py-3 text-left"
        >
          <div className="flex items-baseline justify-between">
            <span className="text-[13px] font-bold text-ink">{t('taskDailyGoal')}</span>
            <span className="flex items-center gap-0.5">
              <span className="tabular text-[13px] font-extrabold text-brand-600">{goalAnim}/{goalLimit}</span>
              <ChevronRight className="w-3.5 h-3.5 text-ink-muted" />
            </span>
          </div>
          {goalLimit <= 12 ? (
            <div className="mt-2 flex gap-1" role="progressbar" aria-valuenow={goalDone} aria-valuemin={0} aria-valuemax={goalLimit}>
              {Array.from({ length: goalLimit }).map((_, i) => (
                <span key={i} className={`h-1 flex-1 rounded-[2px] transition-colors duration-300 ${i < goalAnim ? 'bg-brand-500' : 'bg-black/[0.07]'}`} />
              ))}
            </div>
          ) : (
            <div className="mt-2 h-1 rounded-[2px] bg-black/[0.07] overflow-hidden" role="progressbar" aria-valuenow={goalDone} aria-valuemin={0} aria-valuemax={goalLimit}>
              <div
                className="relative h-full overflow-hidden rounded-[2px] bg-brand-500 transition-[width] duration-150 ease-out"
                style={{ width: `${Math.min(100, Math.round((goalAnim / goalLimit) * 100))}%` }}
              >
                <span className="animate-bar-shine pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-white/70 to-transparent" />
              </div>
            </div>
          )}
          <p className="mt-2 text-[11.5px] text-ink-muted">
            {t('earnedToday')} <span className="tabular font-bold text-money">{formatMoney(summary?.earnedToday ?? 0)}</span>
          </p>
        </button>
      </div>

      {/* Top tasks — plain horizontal rail */}
      {topTasks.length > 0 && (
        <div className="mt-5">
          <h2 className="max-w-md mx-auto px-4 flex items-center gap-1.5 text-[14px] font-bold text-ink mb-2.5">
            <Flame className="w-4 h-4 text-brand-500" />
            {t('taskTopTasks')}
          </h2>
          <div className="max-w-md mx-auto flex gap-2.5 overflow-x-auto scrollbar-hide px-4 pb-1">
            {topTasks.map((task) => (
              <div key={task.id} className="shrink-0">
                <TopTaskCard task={task} rank={topTasks.indexOf(task) + 1} onClick={() => setSelected(task)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Categories — horizontal pill rail, filters the list below */}
      {categories.length > 0 && (
        <div className="mt-5">
          <div className="max-w-md mx-auto overflow-x-auto scrollbar-hide pb-1">
            <div className="flex gap-2 w-max px-4">
              <button
                type="button"
                onClick={() => setCategory(null)}
                className={`shrink-0 px-3.5 py-2 rounded-full text-[12.5px] font-semibold transition-colors ${
                  category === null ? 'bg-ink text-white' : 'bg-white text-ink border border-black/[0.05]'
                }`}
              >
                {t('taskFilter_all')}
              </button>
              {categories.map((cat) => {
                const meta = getCategoryMeta(cat.name);
                const Icon = meta.Icon;
                const active = category === cat.name;
                return (
                  <button
                    key={cat.name}
                    type="button"
                    onClick={() => setCategory(active ? null : cat.name)}
                    className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-[12.5px] font-semibold transition-colors ${
                      active ? 'bg-ink text-white' : 'bg-white text-ink border border-black/[0.05]'
                    }`}
                  >
                    <span className={`w-5 h-5 shrink-0 rounded-[7px] ${meta.tint} flex items-center justify-center`}>
                      <Icon className="w-3 h-3 text-white" />
                    </span>
                    {cat.name}
                    <span className={`tabular text-[11px] ${active ? 'text-white/60' : 'text-ink-muted'}`}>{cat.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Task feed — vertical list, the way real task apps show work */}
      <div className="mt-5">
        <div className="max-w-md mx-auto px-4 flex items-center justify-between mb-2.5">
          <h2 className="text-[14px] font-bold text-ink">
            {t('taskAvailable')}
            <span className="ml-1.5 tabular text-[12px] font-medium text-ink-muted">{listedTasks.length}</span>
          </h2>
          <div className="flex items-center gap-3">
            {SORTS.map(([key, labelKey]) => (
              <button
                key={key}
                type="button"
                onClick={() => setSort(key)}
                className={`text-[12px] transition-colors ${
                  sort === key ? 'font-semibold text-ink' : 'font-medium text-ink-muted'
                }`}
              >
                {t(labelKey)}
              </button>
            ))}
          </div>
        </div>
        {listedTasks.length === 0 ? (
          <div className="max-w-md mx-auto bg-white border-y border-black/[0.05] py-10 text-center">
            <p className="text-sm text-ink-muted">{t('taskEmpty')}</p>
          </div>
        ) : (
          <div className="max-w-md mx-auto bg-white border-y border-black/[0.05] divide-y divide-black/[0.05]">
            {listedTasks.map((task) => <TaskCard key={task.id} task={task} onClick={() => setSelected(task)} />)}
          </div>
        )}
      </div>

      {summary && summary.pendingReview > 0 && (
        <div className="max-w-md mx-auto mt-5">
          <div className="flex items-center gap-2.5 bg-white border-y border-black/[0.05] px-4 py-3">
            <Clock className="w-5 h-5 text-brand-500 shrink-0" />
            <p className="text-[13px] text-ink-secondary flex-1">
              <span className="tabular font-bold text-ink">{summary.pendingReview}</span> {t('taskAwaitingReview')}
            </p>
          </div>
        </div>
      )}

      {/* Products rail — a distinct section from social tasks (advance-payment goods) */}
      {products.length > 0 && (
        <div id="san-pham" className="mt-5 scroll-mt-16">
          <h2 className="max-w-md mx-auto px-4 text-[14px] font-bold text-ink mb-2.5">{t('taskProducts')}</h2>
          <div className="max-w-md mx-auto overflow-x-auto scrollbar-hide pb-1">
            <div className="flex gap-3 w-max px-4">
              {products.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { setSelectedProduct(p); setShowProductModal(true); }}
                  className="w-[124px] shrink-0 text-left bg-white rounded-lg border border-black/[0.05] overflow-hidden active:scale-[0.98] transition-transform"
                >
                  <span className="block h-[104px] bg-canvas overflow-hidden">
                    <img
                      src={API_CONFIG.getImageUrl(p.product_image) || '/images/placeholder.svg'}
                      alt={p.product_name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { e.target.src = '/images/placeholder.svg'; }}
                    />
                  </span>
                  <span className="block p-2.5">
                    <span className="block text-[12px] font-semibold text-ink leading-snug line-clamp-2 min-h-[32px]">{p.product_name}</span>
                    <span className="mt-1.5 block tabular text-[13px] font-extrabold text-ink">{formatMoney(p.price)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-5"><StatsStrip /></div>
      <div className="mt-5"><PartnerScroll /></div>
      <VersionFooter />

      <SupportFab />

      {selected && (
        <TaskDetailSheet
          task={selected}
          onClose={() => setSelected(null)}
          onChanged={async () => { await load(); setSelected(null); }}
          onError={(message) => {
            setToast({ type: 'error', message });
            // 幸运订单刚生成时接口会直接报错，这里顺手刷新一次把卡片显示出来
            load();
          }}
        />
      )}

      {selectedProduct && (
        <ProductDetailModal
          isOpen={showProductModal}
          onClose={() => setShowProductModal(false)}
          product={{
            productName: selectedProduct.product_name,
            productImage: API_CONFIG.getImageUrl(selectedProduct.product_image),
            price: selectedProduct.price
          }}
        />
      )}
    </div>
  );
};

export default TasksPage;
